import React, { useState, useMemo } from "react";
import { Search } from "lucide-react";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { useCatalogsStore } from "../../../store/Catalogs";
import { useCentralStore } from "../../../store/Central";
import { useTemplatesStore } from "../../../store/Templates/templates.store";
import {
	buildCatalogItemFromTariff,
	buildCatalogItemFromArticle,
	buildCatalogItemFromTemplate,
} from "../utils";
import {
	ServiceCatalogBrowserDraggableItem,
	BrowserRow,
} from "./service-catalog-browser-draggable-item.component";

type FilterType = "all" | "tariff" | "article" | "template";

export const ServiceCatalogBrowserPanel: React.FC = () => {
	const [search, setSearch] = useState("");
	const [filter, setFilter] = useState<FilterType>("all");

	const tariffsLookup = useCatalogsStore((s) => s.tariffsLookup);
	const articles = useCentralStore((s) => s.articles);
	const templatesLookup = useTemplatesStore((s) => s.templatesLookup);

	const allRows = useMemo(() => {
		const rows: BrowserRow[] = [];

		if (filter === "all" || filter === "tariff") {
			for (const codeEntries of Object.values(tariffsLookup)) {
				const all = Object.values(codeEntries);
				const tariff = all.find((t) => t.organization_id) || all[0];
				if (!tariff) continue;
				rows.push({
					badge: "T",
					badgeClass: "bg-blue-100 text-blue-700",
					code: tariff.code_e || "",
					description: tariff.description_de || "",
					catalogItem: buildCatalogItemFromTariff(tariff),
				});
			}
		}

		if (filter === "all" || filter === "article") {
			for (const codeEntries of Object.values(articles)) {
				for (const article of Object.values(codeEntries)) {
					rows.push({
						badge: "A",
						badgeClass: "bg-green-100 text-green-700",
						code: article.code_e || "",
						description: article.description_de || "",
						catalogItem: buildCatalogItemFromArticle(article),
					});
				}
			}
		}

		if (filter === "all" || filter === "template") {
			for (const template of Object.values(templatesLookup)) {
				rows.push({
					badge: "V",
					badgeClass: "bg-purple-100 text-purple-700",
					code: template.code || "",
					description: template.name || "",
					catalogItem: buildCatalogItemFromTemplate(template),
				});
			}
		}

		return rows;
	}, [tariffsLookup, articles, templatesLookup, filter]);

	const filteredRows = useMemo(() => {
		if (!search.trim()) return allRows;
		const q = search.toLowerCase();
		return allRows.filter(
			(r) =>
				r.code.toLowerCase().includes(q) ||
				r.description.toLowerCase().includes(q)
		);
	}, [allRows, search]);

	const filterButtons: { label: string; value: FilterType }[] = [
		{ label: "Alle", value: "all" },
		{ label: "Tarife", value: "tariff" },
		{ label: "Artikel", value: "article" },
		{ label: "Vorlagen", value: "template" },
	];

	return (
		<div className="p-4 flex flex-col h-full">
			<div className="relative mb-3">
				<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
				<Input
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					placeholder="Suchen..."
					className="pl-9 h-9"
					aria-label="Positionen suchen"
					data-testid="catalog-browser-search"
				/>
			</div>

			<div className="flex gap-1 mb-3">
				{filterButtons.map((f) => (
					<Button
						key={f.value}
						variant={filter === f.value ? "default" : "outline"}
						size="sm"
						className="text-xs h-7"
						onClick={() => setFilter(f.value)}
						data-testid={`catalog-browser-filter-${f.value}`}
					>
						{f.label}
					</Button>
				))}
			</div>

			<div className="flex-1 overflow-auto -mx-4 px-4">
				{filteredRows.length === 0 ? (
					<p className="text-sm text-muted-foreground text-center py-8">
						Keine Ergebnisse gefunden
					</p>
				) : (
					filteredRows.map((row, i) => (
						<ServiceCatalogBrowserDraggableItem
							key={`${row.badge}-${row.code}-${i}`}
							id={`browser-${row.badge}-${row.code}-${i}`}
							row={row}
						/>
					))
				)}
			</div>
		</div>
	);
};
